import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  getVisitorId,
  getSessionId,
  captureAttribution,
  buildEventPayload,
  sendTrackingEvent,
  pushToDataLayer,
  getMappedEventName,
  getAttribution,
} from '@/lib/tracker';

const TrackingContext = createContext(null);

// Cookie helpers
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
};

// Default consent state
const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  personalization: false,
  marketing: false,
  hasInteracted: false,
  timestamp: null,
  version: 2,
};

// Consent expires after 12 months — banner re-prompts after that
const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;
const CURRENT_CONSENT_VERSION = 2;

export function TrackingProvider({ children }) {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [isConsentBannerVisible, setIsConsentBannerVisible] = useState(false);
  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [debugEvents, setDebugEvents] = useState([]);
  const initDone = useRef(false);

  // Initialize tracking on mount
  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;

    // Capture attribution from URL params
    captureAttribution();

    // Initialize visitor/session
    getVisitorId();
    getSessionId();
  }, []);

  // Load consent from cookie on mount
  useEffect(() => {
    const storedConsent = getCookie('cookie_consent');
    if (storedConsent) {
      try {
        const parsed = JSON.parse(decodeURIComponent(storedConsent));
        // Re-prompt if consent is expired (>12 months) or schema version changed
        const expired = parsed.timestamp && (Date.now() - new Date(parsed.timestamp).getTime() > CONSENT_TTL_MS);
        const versionStale = (parsed.version || 1) < CURRENT_CONSENT_VERSION;
        if (expired || versionStale) {
          setIsConsentBannerVisible(true);
        } else {
          setConsent({
            necessary: true,
            analytics: !!parsed.analytics,
            personalization: !!parsed.personalization,
            marketing: !!parsed.marketing,
            hasInteracted: true,
            timestamp: parsed.timestamp,
            version: parsed.version || CURRENT_CONSENT_VERSION,
          });
        }
      } catch (e) {
        setIsConsentBannerVisible(true);
      }
    } else {
      setIsConsentBannerVisible(true);
    }
  }, []);

  // Save consent to cookie
  const saveConsent = useCallback((newConsent) => {
    const consentToSave = {
      necessary: true,
      analytics: !!newConsent.analytics,
      personalization: !!newConsent.personalization,
      marketing: !!newConsent.marketing,
      hasInteracted: true,
      timestamp: new Date().toISOString(),
      version: CURRENT_CONSENT_VERSION,
    };
    setCookie('cookie_consent', encodeURIComponent(JSON.stringify(consentToSave)));
    setConsent(consentToSave);
    setIsConsentBannerVisible(false);

    // Update Google Consent Mode v2
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': consentToSave.analytics ? 'granted' : 'denied',
        'ad_storage': consentToSave.marketing ? 'granted' : 'denied',
        'ad_user_data': consentToSave.marketing ? 'granted' : 'denied',
        'ad_personalization': consentToSave.marketing ? 'granted' : 'denied',
        'personalization_storage': consentToSave.personalization ? 'granted' : 'denied',
      });
    }
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({ analytics: true, personalization: true, marketing: true });
  }, [saveConsent]);

  const rejectAll = useCallback(() => {
    saveConsent({ analytics: false, personalization: false, marketing: false });
  }, [saveConsent]);

  const setCustomConsent = useCallback((settings) => {
    saveConsent({
      analytics: settings.analytics ?? false,
      personalization: settings.personalization ?? false,
      marketing: settings.marketing ?? false,
    });
  }, [saveConsent]);

  const [consentForceVisible, setConsentForceVisible] = useState(false);

  const showConsentSettings = useCallback(() => {
    setConsentForceVisible(true);
    setIsConsentBannerVisible(true);
  }, []);

  const closeConsentBanner = useCallback(() => {
    setIsConsentBannerVisible(false);
    setConsentForceVisible(false);
  }, []);

  // ─── Core Tracking Function ─────────────────────────────
  const trackEvent = useCallback((eventName, listingData = {}, extra = {}) => {
    const consentState = {
      necessary: consent.necessary,
      analytics: consent.analytics,
      marketing: consent.marketing,
    };

    // Build full event payload
    const payload = buildEventPayload(eventName, listingData, consentState);
    if (extra) payload.extra = extra;

    // Always push to dataLayer (GTM handles consent internally)
    const dlEventName = getMappedEventName(eventName, 'dataLayer');
    pushToDataLayer(dlEventName, {
      event_id: payload.event_id,
      visitor_id: payload.visitor_id,
      session_id: payload.session_id,
      listing_id: payload.listing_id,
      company_name: payload.company_name,
      category: payload.category,
      city: payload.city,
      state: payload.state,
      ...extra,
    });

    // Always send to our first-party backend (consent stored with event)
    sendTrackingEvent(payload);

    // Fire GA4 event if consent allows
    if (window.gtag && consent.analytics) {
      const ga4Event = getMappedEventName(eventName, 'ga4');
      window.gtag('event', ga4Event, {
        event_id: payload.event_id,
        content_id: payload.listing_id,
        content_name: payload.company_name,
        content_category: payload.category,
        ...extra,
      });
    }

    // Fire Meta Pixel if consent allows
    if (window.fbq && consent.marketing) {
      const metaEvent = getMappedEventName(eventName, 'meta');
      window.fbq('trackCustom', metaEvent, {
        content_ids: payload.listing_id ? [payload.listing_id] : [],
        content_name: payload.company_name,
        content_category: payload.category,
        ...extra,
      });
    }

    // Log for debug panel
    setDebugEvents(prev => [
      { event_name: eventName, event_id: payload.event_id, timestamp: new Date().toISOString(), listing_id: payload.listing_id, ...extra },
      ...prev.slice(0, 49),
    ]);
  }, [consent]);

  // ─── Convenience Methods ────────────────────────────────
  const trackListingView = useCallback((listing) => {
    trackEvent('listing_view', listing);
  }, [trackEvent]);

  const trackCallClick = useCallback((listing) => {
    trackEvent('call_click', listing, { action: 'phone_call' });
  }, [trackEvent]);

  const trackBookingClick = useCallback((listing) => {
    trackEvent('booking_click', listing, { action: 'booking' });
  }, [trackEvent]);

  const trackWebsiteClick = useCallback((listing, url) => {
    trackEvent('website_click', listing, { target_url: url });
  }, [trackEvent]);

  const trackFormSubmit = useCallback((listing, formData = {}) => {
    trackEvent('form_submit', listing, formData);
  }, [trackEvent]);

  const trackChatClick = useCallback((listing) => {
    trackEvent('chat_click', listing, { action: 'chat_start' });
  }, [trackEvent]);

  const trackOfferClick = useCallback((listing, offerTitle) => {
    trackEvent('offer_click', listing, { offer_title: offerTitle });
  }, [trackEvent]);

  const trackDirectionsClick = useCallback((listing) => {
    trackEvent('directions_click', listing, { action: 'get_directions' });
  }, [trackEvent]);

  // Legacy methods (backward-compatible)
  const trackPageView = useCallback((pageData = {}) => {
    if (!consent.analytics) return;
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
        ...pageData,
      });
    }
    if (window.fbq && consent.marketing) {
      window.fbq('track', 'PageView');
    }
  }, [consent.analytics, consent.marketing]);

  const trackViewContent = useCallback((contentData) => {
    // Fire the new tracking system
    if (contentData.business_id || contentData.content_id) {
      trackEvent('listing_view', {
        id: contentData.business_id || contentData.content_id,
        name: contentData.business_name || contentData.content_name,
        category_name: contentData.category || contentData.listing_type,
        city: contentData.location?.split(',')[0]?.trim(),
      }, {
        content_type: contentData.content_type,
        is_featured: contentData.is_featured,
        is_best_of_best: contentData.is_best_of_best,
      });
    }
  }, [trackEvent]);

  const trackLead = useCallback((leadData) => {
    if (!consent.marketing) return;
    const { business_id, business_name, content_type, value, ...customData } = leadData;
    trackEvent('form_submit', {
      id: business_id,
      name: business_name,
    }, { value, content_type, ...customData });
  }, [consent.marketing, trackEvent]);

  const setBusinessContext = useCallback((business) => {
    setCurrentBusiness(business);
    if (business?.tracking) {
      const { meta_pixel_id, ga4_measurement_id } = business.tracking;
      if (meta_pixel_id && window.fbq && consent.marketing) {
        window.fbq('init', meta_pixel_id);
      }
    }
  }, [consent.marketing]);

  const value = {
    // Consent state
    consent,
    isConsentBannerVisible,
    consentForceVisible,
    
    // Consent actions
    acceptAll,
    rejectAll,
    setCustomConsent,
    showConsentSettings,
    closeConsentBanner,
    
    // Core tracking
    trackEvent,
    
    // Convenience methods
    trackListingView,
    trackCallClick,
    trackBookingClick,
    trackWebsiteClick,
    trackFormSubmit,
    trackChatClick,
    trackOfferClick,
    trackDirectionsClick,
    
    // Legacy methods (backward-compatible)
    trackPageView,
    trackViewContent,
    trackLead,
    
    // Business context
    currentBusiness,
    setBusinessContext,
    
    // Debug
    debugEvents,
    
    // Attribution helper
    getAttribution,
    
    // Identity
    getVisitorId,
    getSessionId,
  };

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
}

export function useTracking() {
  const context = useContext(TrackingContext);
  if (!context) {
    throw new Error('useTracking must be used within a TrackingProvider');
  }
  return context;
}

export default TrackingContext;
